// src/hooks/useStatementImports.js
// Supabase CRUD hook for the statement_imports table.
// Follows the field-locked pattern from useSupabase.js.

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { formatSupabaseError } from './useSupabase';

const requireSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw new Error('Authentication error: ' + error.message);
  if (!session || !session.user) throw new Error('Please sign in to perform this action.');
  return session.user.id;
};

const mapRow = (r) => ({
  id:                 r.id,
  batchId:            r.batch_id,
  format:             r.format,
  formatLabel:        r.format_label || r.format,
  officeLocation:     r.office_location || 'NL',
  currency:           r.currency || 'EUR',
  totalRows:          r.total_rows ?? 0,
  brioRows:           r.brio_rows ?? 0,
  personalRows:       r.personal_rows ?? 0,
  importedRows:       r.imported_rows ?? 0,
  errorCount:         r.error_count ?? 0,
  brioTotal:          r.brio_total ?? 0,
  personalTotal:      r.personal_total ?? 0,
  importedBy:         r.imported_by || null,
  importedAt:         r.imported_at,
  notes:              r.notes || '',
  updatedAt:          r.updated_at || null,
  receiptLinkedCount: r.receipt_linked_count ?? 0,
  receiptMissingCount: r.receipt_missing_count ?? 0,
});

export default function useStatementImports() {
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchImports = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error: fetchErr } = await supabase
        .from('statement_imports')
        .select('*')
        .order('imported_at', { ascending: false });

      if (fetchErr) throw fetchErr;
      setImports((data || []).map(mapRow));
      setError(null);
    } catch (err) {
      console.error('[useStatementImports] fetch error:', err);
      setError(formatSupabaseError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchImports(); }, [fetchImports]);

  const updateImport = useCallback(async (id, updates) => {
    await requireSession();
    const db = { updated_at: new Date().toISOString() };
    if (updates.notes !== undefined) db.notes = updates.notes;
    if (updates.receiptLinkedCount !== undefined) db.receipt_linked_count = updates.receiptLinkedCount;
    if (updates.receiptMissingCount !== undefined) db.receipt_missing_count = updates.receiptMissingCount;

    const { error: updateErr } = await supabase
      .from('statement_imports')
      .update(db)
      .eq('id', id);

    if (updateErr) return { success: false, error: formatSupabaseError(updateErr) };
    await fetchImports();
    return { success: true };
  }, [fetchImports]);

  // Fetch expenses belonging to a specific import batch (from BOTH tables)
  const fetchBatchExpenses = useCallback(async (batchId) => {
    // Query project expenses from expenses table
    const { data: expData, error: expErr } = await supabase
      .from('expenses')
      .select('*')
      .eq('import_batch_id', batchId)
      .order('date', { ascending: true });

    if (expErr) {
      console.error('[useStatementImports] batch expenses error:', expErr);
    }

    const projectRows = (expData || []).map(e => ({
      id:                    e.id,
      projectId:             e.project_id,
      category:              e.category || '',
      description:           e.description || '',
      amount:                e.amount ?? 0,
      date:                  e.date,
      status:                e.status || 'planned',
      officeLocation:        e.office_location || 'NL',
      amountOriginal:        e.amount_original ?? e.amount,
      currencyOriginal:      e.currency_original || 'INR',
      fxRateToInr:           e.fx_rate_to_inr ?? 1,
      amountInInr:           e.amount_in_inr ?? e.amount,
      importSource:          e.import_source,
      importBatchId:         e.import_batch_id,
      linkedReimbursementId: e.linked_reimbursement_id || null,
      receiptStatus:         e.receipt_status || 'missing',
      _sourceTable:          'expenses',
    }));

    // Query office overheads from office_overheads_yearly table
    const { data: ohData, error: ohErr } = await supabase
      .from('office_overheads_yearly')
      .select('*')
      .eq('import_batch_id', batchId)
      .order('expense_date', { ascending: true });

    if (ohErr) {
      console.error('[useStatementImports] batch office overheads error:', ohErr);
    }

    const officeRows = (ohData || []).map(o => ({
      id:                    o.id,
      projectId:             null,
      category:              o.category || '',
      description:           o.subcategory || o.notes || '',
      amount:                o.amount ?? 0,
      date:                  o.expense_date || `${o.year}-01-01`,
      status:                'planned',
      officeLocation:        o.office_location || 'NL',
      amountOriginal:        o.amount_original ?? o.amount,
      currencyOriginal:      o.currency_original || 'INR',
      fxRateToInr:           o.fx_rate_to_inr ?? 1,
      amountInInr:           o.amount_in_inr ?? o.amount,
      importSource:          o.import_source,
      importBatchId:         o.import_batch_id,
      linkedReimbursementId: null,
      receiptStatus:         'missing',
      _sourceTable:          'office_overheads_yearly',
    }));

    // Merge and sort by date
    return [...projectRows, ...officeRows].sort((a, b) =>
      (a.date || '').localeCompare(b.date || '')
    );
  }, []);

  // Delete an import batch and all its associated expenses/overheads
  const deleteImport = useCallback(async (id, batchId) => {
    try {
      await requireSession();

      // 1. Unlink any reimbursements referencing expenses from this batch
      const { data: batchExpenses } = await supabase
        .from('expenses')
        .select('id')
        .eq('import_batch_id', batchId);

      if (batchExpenses && batchExpenses.length > 0) {
        const expenseIds = batchExpenses.map(e => e.id);
        await supabase
          .from('reimbursements')
          .update({ linked_expense_id: null })
          .in('linked_expense_id', expenseIds);
      }

      // 2. Delete imported expenses for this batch
      const { error: expDelErr } = await supabase
        .from('expenses')
        .delete()
        .eq('import_batch_id', batchId);
      if (expDelErr) console.warn('[deleteImport] expenses delete:', expDelErr.message);

      // 3. Delete imported office overheads for this batch
      const { error: ohDelErr } = await supabase
        .from('office_overheads_yearly')
        .delete()
        .eq('import_batch_id', batchId);
      if (ohDelErr) console.warn('[deleteImport] office overheads delete:', ohDelErr.message);

      // 4. Delete the import record itself
      const { error: importDelErr } = await supabase
        .from('statement_imports')
        .delete()
        .eq('id', id);
      if (importDelErr) throw importDelErr;

      // Optimistically remove from local state, then full refresh
      setImports(prev => prev.filter(i => i.id !== id));
      await fetchImports();
      return { success: true };
    } catch (err) {
      console.error('[useStatementImports] delete error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'import' }) };
    }
  }, [fetchImports]);

  return {
    imports,
    loading,
    error,
    refresh: fetchImports,
    updateImport,
    deleteImport,
    fetchBatchExpenses,
  };
}
